import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonTitle, IonToolbar, IonToast, IonIcon, IonSpinner } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import DeleteProfile from '@/components/DeleteProfile';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { useState, useEffect } from 'react';
import { logoGoogle, logoApple } from 'ionicons/icons';

const AccountSettings = () => {
  const { t } = useTranslation(['MGP', 'Settings']);
  const [user, setUser] = useState<any>(null);
  const [linkedProviders, setLinkedProviders] = useState<string[]>([]);
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  const [isLinkingApple, setIsLinkingApple] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState('success');

  const refreshUserData = async () => {
    try {
      const result = await FirebaseAuthentication.getCurrentUser();
      setUser(result.user);
      
      // Extract linked providers from providerData
      const providers = result.user?.providerData?.map(provider => provider.providerId) || [];
      setLinkedProviders(providers);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    refreshUserData();
  }, []);

  return (
    <>
      <IonHeader>
        <IonToolbar className="md:hidden">
          <IonButtons slot="start">
            <IonBackButton text={t('back')} defaultHref="/settings" />
          </IonButtons>
          <IonTitle>{t('my_account')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <h1 className="hidden md:block text-3xl font-bold pt-6">{t('my_account')}</h1>
        <div className="space-y-4 p-2">
          <Card className='border-input'>
            <CardContent className="space-y-4 pt-4">
              <div className="flex items-center space-x-4">
                <Avatar className="size-16">
                  <AvatarImage src={user?.photoURL || undefined} />
                  <AvatarFallback>{user?.email?.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-medium">{user?.displayName || t('unnamed_user')}</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">{t('account_info')}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">{t('email_verified')}</div>
                  <div>{user?.emailVerified ? t('yes') : t('no')}</div>
                  <div className="text-muted-foreground">{t('primary_provider')}</div>
                  <div>{user?.providerData[0]?.providerId || t('unknown')}</div>
                  <div className="text-muted-foreground">{t('created')}</div>
                  <div>{user?.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : t('unknown')}</div>
                </div>
              </div>
              
              <div className="space-y-2 pt-4">
                <h4 className="font-medium">{t('linked_accounts')}</h4>
                <p className="text-sm text-muted-foreground">{t('link_accounts_description')}</p>
                
                <div className="flex flex-col space-y-3 pt-2">
                  {/* Google Provider */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <IonIcon icon={logoGoogle} className="text-2xl" />
                      <span>Google</span>
                    </div>
                    {linkedProviders.includes('google.com') ? (
                      <div className="text-green-600 text-sm font-medium">{t('linked')}</div>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex items-center space-x-1"
                        onClick={async () => {
                          try {
                            setIsLinkingGoogle(true);
                            await FirebaseAuthentication.linkWithGoogle();
                            await refreshUserData();
                            setToastMessage(t('google_account_linked'));
                            setToastColor('success');
                            setShowToast(true);
                          } catch (error: any) {
                            console.error('Error linking Google account:', error);
                            setToastMessage(error.message || t('error_linking_account'));
                            setToastColor('danger');
                            setShowToast(true);
                          } finally {
                            setIsLinkingGoogle(false);
                          }
                        }}
                        disabled={isLinkingGoogle}
                      >
                        {isLinkingGoogle ? <IonSpinner name="dots" /> : t('link')}
                      </Button>
                    )}
                  </div>
                  
                  {/* Apple Provider */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <IonIcon icon={logoApple} className="text-2xl" />
                      <span>Apple</span>
                    </div>
                    {linkedProviders.includes('apple.com') ? (
                      <div className="text-green-600 text-sm font-medium">{t('linked')}</div>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex items-center space-x-1"
                        onClick={async () => {
                          try {
                            setIsLinkingApple(true);
                            await FirebaseAuthentication.linkWithApple();
                            await refreshUserData();
                            setToastMessage(t('apple_account_linked'));
                            setToastColor('success');
                            setShowToast(true);
                          } catch (error: any) {
                            console.error('Error linking Apple account:', error);
                            setToastMessage(error.message || t('error_linking_account'));
                            setToastColor('danger');
                            setShowToast(true);
                          } finally {
                            setIsLinkingApple(false);
                          }
                        }}
                        disabled={isLinkingApple}
                      >
                        {isLinkingApple ? <IonSpinner name="dots" /> : t('link')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

            </CardContent>
          </Card>
          <DeleteProfile />

        </div>
        
        {/* Toast for success/error messages */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
          position="bottom"
        />
      </IonContent>
    </>
  );
};

export default AccountSettings;
